<script lang="ts" generics="TValues, TValue">
    import { createSelect, melt, createSync, type SelectOption } from '@melt-ui/svelte';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { quadOut } from 'svelte/easing';
    import { fly } from 'svelte/transition';
    import { cx } from '$lib/utils/cx';
    import castArray from 'lodash/castArray';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue[]>;
    export let placeholder: string = '';
    export let id: string | null = null;
    export let options: readonly SelectOption<TValue>[] = [];
    export let max: number | null = null;

    export let valueToOption: (v: TValue) => SelectOption<TValue>;

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    const {
        elements: { trigger, menu, option, hiddenInput },
        states: { selectedLabel, selected, open },
        helpers: { isSelected, isHighlighted },
    } = createSelect<TValue, boolean>({
        forceVisible: true,
        multiple: true,
        positioning: {
            placement: 'bottom',
            sameWidth: true,
            gutter: 6,
        },
        ids: {
            trigger: id ?? form.getFieldId(field),
        },
    });

    $: values = form.stores().fieldValue(field);

    const sync = createSync({ selected });
    $: sync.selected(
        $values.map((v) => valueToOption(v)),
        (selected) => {
            let values = castArray(selected ?? []).map((o) => o.value);
            form.dispatch(
                form
                    .actions()
                    .changeValue(max === null ? values : values.slice(0, max), field),
            );
        },
    );

    $: $open ? onFocus() : onBlur();

    interface $$Slots {
        label: {
            option: SelectOption<TValue>;
            selected: boolean;
            highlighted: boolean;
        };
    }
</script>

<button
    class={cx(
        'btn-reset',
        'control-form',
        'flex justify-between items-center',
        'bg-white',
        'w-full',
        'text-left',
        'min-h-[47px]',
    )}
    use:melt={$trigger}
>
    <span class={`line-clamp-1 ${$selectedLabel ? '' : 'text-gray-600'}`}>
        {$selectedLabel || placeholder}
    </span>
    <span
        class={`${$open ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} text-gray-800 text-xs`}
    />
</button>

<input use:melt={$hiddenInput} />

{#if $open}
    <StackZIndex zIndex="inc" let:zIndex>
        <div
            class={cx(
                'overflow-y-auto',
                'flex flex-col',
                'rounded-[4px]',
                'p-[6px]',
                'bg-white',
                'shadow-leo focus:ring-0',
                'z-[--z-index]',
            )}
            {...$menu}
            style={`${$menu.style}; --z-index: ${zIndex}`}
            use:menu
            transition:fly={{ duration: 150, easing: quadOut, y: '-10px', opacity: 0 }}
        >
            {#each options as o}
                <div
                    class={cx(
                        'group',
                        'flex gap-[8px]',
                        'px-[8px] py-[4px]',
                        'cursor-pointer',
                        'rounded-[2px]',
                        'data-[highlighted]:bg-gray-200',
                        'data-[disabled]:opacity-50',
                    )}
                    use:melt={$option({
                        ...o,
                        disabled:
                            max === null || $isSelected(o.value)
                                ? false
                                : $values.length >= max,
                    })}
                >
                    <div class="flex items-center justify-center">
                        <div
                            class={cx(
                                'force-size-[16px] shrink-0',
                                'rounded-[2px]',
                                'border-[1px] border-solid border-brand-blue',
                                'group-data-[selected]:bg-brand-blue',
                                'flex items-center justify-center',
                            )}
                        >
                            <i
                                class="hidden group-data-[selected]:block tnp tnp-check text-white text-10"
                                aria-hidden={true}
                            >
                            </i>
                        </div>
                    </div>

                    <slot
                        name="label"
                        option={o}
                        selected={$isSelected(o.value)}
                        highlighted={$isHighlighted(o.value)}
                    >
                        <div>{o.label}</div>
                    </slot>
                </div>
            {/each}
        </div>
    </StackZIndex>
{/if}
